/**
 * Modal registry — declarative auto-init from `data-datte-*` attributes.
 *
 * The "infra over custom JS" core: on `DOMContentLoaded` (driven by the thin
 * entrypoint `src-js/modal/modal.ts`) it finds every `.datte-modal`, parses its
 * opt-in attributes into a `ModalRegistryConfig`, and constructs one
 * `ModalController` per node. A new modal that needs only standard cross-cutting
 * behaviour therefore needs ZERO new TypeScript — just the partial include with
 * attributes (target G4, §7).
 *
 * `data-datte-fullscreen` is intentionally NOT wired here: it is a pure-CSS hook
 * (`.datte-modal[data-datte-fullscreen] { @include ... }`), so the registry skips
 * it — that is the payoff of presence-based attributes (target §3.3).
 *
 * @see docs/architecture/modals/target-architecture.md §3.3 (registry + vocabulary)
 */

import { ModalController, type ModalControllerConfig } from './modal-controller';

export type ModalRegistryConfig = ModalControllerConfig;

/** Marks an element the registry has already wired, so re-scans are idempotent. */
const INITIALISED_FLAG = 'datteModalInitialised';

/**
 * Read a presence-based boolean opt-in attribute.
 *
 * Fail-loud on a non-empty value: the vocabulary is presence-based (the attribute
 * is the opt-in, its value is meaningless), so a stray value like
 * `data-datte-focus-trap="false"` almost certainly means the author expected
 * value-based semantics and is silently getting the opposite. We warn and treat
 * presence as the opt-in (the documented contract) rather than guessing.
 */
function readPresenceFlag(el: HTMLElement, attr: string): boolean {
  if (!el.hasAttribute(attr)) return false;
  const value = el.getAttribute(attr);
  if (value !== null && value !== '' && value !== attr) {
    console.warn(
      `[ModalRegistry] ${attr} is a presence-based opt-in; its value (${JSON.stringify(value)}) is ignored. ` +
        `Remove the value or the attribute; do not rely on value semantics.`
    );
  }
  return true;
}

/**
 * Parse a modal element's `data-datte-*` opt-ins into a controller config.
 * Pure (reads only the element's attributes) — the prime unit-test target.
 *
 * Note: `data-datte-fullscreen` is a CSS-only hook and deliberately absent from
 * the config (the controller has no fullscreen behaviour to wire).
 */
export function parseRegistryConfig(el: HTMLElement): ModalRegistryConfig {
  return {
    historyBack: readPresenceFlag(el, 'data-datte-history-back'),
    viewportSync: readPresenceFlag(el, 'data-datte-viewport-sync'),
    focusTrap: readPresenceFlag(el, 'data-datte-focus-trap'),
  };
}

/**
 * Scan `root` for `.datte-modal` nodes and construct a controller for each.
 * Idempotent — already-initialised elements are skipped, so a re-scan after a
 * dynamic include is safe. Returns the controllers constructed in THIS call.
 */
export function initModalRegistry(root: ParentNode = document): ModalController[] {
  const controllers: ModalController[] = [];

  root.querySelectorAll<HTMLElement>('.datte-modal').forEach((el) => {
    if (el.dataset[INITIALISED_FLAG] === 'true') return;
    el.dataset[INITIALISED_FLAG] = 'true';

    controllers.push(new ModalController(el, parseRegistryConfig(el)));
  });

  return controllers;
}
